import { localCardSchema } from '../db/local-card-schema'
import type { DatabaseDriver } from '@w/db'
import type { TursoWorkerDatabaseDriver } from '@w/db-turso-browser-adapter/worker'
import { nullTel, type Tel } from '@w/tel'
import { readReplicaState, ReplicaTransportError } from '@w/db-turso-browser-adapter/worker'
import { completeLearnerBootstrap } from '../db/learner-actions'
import { createClientDb } from '../db/db'
import { isDeletedWorkspaceReset, resetSpaceReplicaWhenIdle } from './space-generation-reset'
import { pendingStore, persistPendingWork } from '../db/reexpress-pending'

/** Browser and full-app tests share all startup SQL and its ordering.
 * The environment supplies the database driver and binds its replica transport.
 */
export async function startSpaceReplica(input: {
  driver: DatabaseDriver
  bindReplicaTransport?: (identity?: Uint8Array) => Promise<void>
  validateReplicaTransport?: () => Promise<void>
  userId?: string
  tel?: Tel
  signal?: AbortSignal
  generationReset?: 'reopen'
}) {
  const { driver, signal } = input
  const tel = input.tel ?? nullTel
  // Startup SQL retains the operation boundary above its native callees.
  const query = <Row>(sql: string) => tel.span({ name: 'db.execute', input: { sql } }, sqlTel =>
    driver.query<Row>(sql, undefined, { tel: sqlTel }))
  signal?.throwIfAborted()
  const replicaDriver = driver as TursoWorkerDatabaseDriver
  if (!replicaDriver.exchange) throw new Error('Native replica exchange required')
  let synced = false
  try {
    // Inside the try: an admission refused with 410 resets like a refused exchange (the browser opener handles it
    // before construction, spaces-browser.ts retryConstructorReset).
    await input.bindReplicaTransport?.()
    await driver.exec(localCardSchema)
    // Replicated tables arrive from the server catalog; genericCards is excluded from replica capture.
    if (input.userId) await completeLearnerBootstrap(createClientDb(driver), input.userId)
    const state = await readReplicaState(replicaDriver)
    if (!state.initialReplayComplete) { await replicaDriver.exchange({ tel, signal }); synced = true }
    await input.validateReplicaTransport?.()
  } catch (error) {
    signal?.throwIfAborted()
    if (!(error instanceof ReplicaTransportError && error.status === 410) || isDeletedWorkspaceReset(error)) throw error
    if (input.generationReset === 'reopen') throw error
    if (!replicaDriver.resetReplica) throw new Error('Native replica reset required', { cause: error })
    const reset = replicaDriver.resetReplica.bind(replicaDriver)
    // db-crdt-sync spec :37: unsynced work is saved before the reset and re-expressed after the download (spaces-resources reexpressPersisted).
    await persistPendingWork(pendingStore(driver))
    const identity = crypto.getRandomValues(new Uint8Array(16))
    await input.bindReplicaTransport?.(identity)
    signal?.throwIfAborted()
    await resetSpaceReplicaWhenIdle(replicaDriver, () => reset(identity, { tel }))
    synced = true
  }
  // Resolve every required table/column in one request without reading rows.
  await query('SELECT p.id,c.userId,c.qid,e.id FROM pages p CROSS JOIN flashcards c CROSS JOIN flashcardEvents e LIMIT 0')
  signal?.throwIfAborted()
  return { synced }
}
